import { Button, Empty, Pagination, Tabs, Tag, Skeleton } from 'antd';
import { Fragment, useEffect, useMemo } from 'react';
import lodash from 'lodash';
const {
  chain,
  difference
} = lodash; // tree-shaking(?) bugs `chain`
import lunr from 'lunr';
import unidecode from 'unidecode-plus';
import { AssignButton, PreviewButton } from '../assignments';
import AC from '../AccessControl';
import cmsFactory from '../cmsFactory';
import LanguageIndicator from '../ui/LanguageIndicator';
import Thumbnail from '../ui/Thumbnail';
import getResourceAssignmentData from '../../helpers/getResourceAssignmentData';
import htmlStringToReactNodes from '../../helpers/htmlStringToReactNodes';
import { useHistoryState, useSession } from '../../lib/hooks';
import { CONTENT_KEYWORD_FILTER_BY_STATE } from '../../helpers/constants';
import styles from './Search.module.scss';
export const languageOptions = [{
  label: 'English',
  value: 'en',
  searchVal: ['en', 'en_es']
}, {
  label: 'Spanish',
  value: 'es',
  searchVal: ['es', 'en_es']
}];
const simOnlySorters = {
  Skill: [...['skills', 'subDomains', 'domains'].map(field => e => -(e.doc.activities?.flatMap(g => g[field]) ?? e.doc[field] ?? []).filter(s => e.filtersApplied[field].includes(s)).length / e.doc[field]?.length), e => -['Lesson', 'Game', 'Book'].reverse().indexOf(e.doc.entity)]
};
const sorters = {
  Relevance: [
  // Count query terms that matched in a high-signal field (name/title OR keywords),
  // so keyword-tagged content isn't gated out by name-only matching (TECH-156).
  e => -Object.values(e.queryMatchData.metadata).filter(m => m.name || m.searchNames || m.keywords).length,
  // Lunr score (which already weights name & keywords equally) ranks within the same
  // match count, ahead of entity-type grouping, so a strong keyword match can surface.
  e => -e.score, e => -['Lesson', 'Book', 'Game'].reverse().indexOf(e.doc.entity)],
  Name: [e => e.doc.name.toLowerCase()],
  ...simOnlySorters
};
export const simOnlySortByOptions = Object.keys(simOnlySorters);
export const sortByOptions = Object.keys(sorters).filter(k => !simOnlySortByOptions.includes(k));
const pageSize = 25;
const SearchView = ({
  contentData,
  tab,
  showAssignBtn,
  showPreviewBtn
}) => {
  const [page, setPage] = useHistoryState(`searchpage-page-${tab}`, 1);
  const pageResults = useMemo(() => contentData.slice((page - 1) * pageSize, page * pageSize), [contentData, page]);
  return <>
			<div style={{
      textAlign: 'right'
    }}>
				<Pagination size="small" showTotal={(total, [begin, end]) => `Showing ${begin} - ${end} of ${total}`} total={contentData.length} pageSize={pageSize} current={page} onChange={p => setPage(p)} showSizeChanger={false} simple data-sentry-element="Pagination" data-sentry-source-file="Search.jsx" />
			</div>
			<ul className={styles["search-result-list"]} children={pageResults.map(datum => {
      const name = [datum.language.includes('en') && <span key="en" children={datum.name} />, datum.language.includes('es') && <span key="es" className="secondary-title" children={datum.spanishName ?? datum.name} />].filter(Boolean).flatMap(e => [e, ' ']).slice(0, -1);
      const learningObjective = [datum.language.includes('en') && <div key="en" children={htmlStringToReactNodes(datum.learningObjectiveHTML)} />, datum.language.includes('es') && <div key="es" className={styles["secondary-learningObjective"]} children={htmlStringToReactNodes(datum.spanishLearningObjectiveHTML ?? datum.learningObjectiveHTML)} />].filter(Boolean);
      const link = {
        pathname: `/toolkits/${datum.type}/[slug]`,
        query: {
          slug: datum.slug
        }
      };
      const linkLabel = <h1 children={name} />;
      const assignmentData = getResourceAssignmentData(datum);
      return <li key={`${datum.entity}:${datum.id}`}>
								<article>
									<Thumbnail data={datum} language={datum.linkedLanguage} link={link} />
									<header>
										<div children={showAssignBtn && assignmentData ? linkLabel : <AC.Link label={linkLabel} href={link} />} />
										<div children={learningObjective} />
										<div>
											<Tag size="large" color="purple" children={datum.entity} />
											<div className="subtitle" children={datum.grades?.map(g => g.name).join(', ')} />
										</div>
									</header>
									<aside>
										<div className={styles["language-indicators"]}>
											<LanguageIndicator languages={datum.language} />
										</div>
										{showPreviewBtn && assignmentData && <PreviewButton assignment={assignmentData} tag={datum.entity} />}
										{showAssignBtn && assignmentData && <AssignButton assignment={assignmentData} type="primary" />}
									</aside>
								</article>
							</li>;
    })} />
			<div style={{
      textAlign: 'right'
    }}>
				<Pagination size="small" showTotal={(total, [begin, end]) => `Showing ${begin} - ${end} of ${total}`} total={contentData.length} pageSize={pageSize} current={page} onChange={p => setPage(p)} showSizeChanger={false} simple data-sentry-element="Pagination" data-sentry-source-file="Search.jsx" />
			</div>
		</>;
};
const tokenizer = txt => lunr.tokenizer(unidecode(txt?.replace(/(<([^>]+)>)/gi, '')).toLowerCase());
const Search = ({
  data: {
    SearchFields,
    content,
    lunrIndex
  },
  query = "",
  languages = [],
  grades = [],
  skills = [],
  subDomains = [],
  domains = [],
  hideEmptyTabs = false,
  onlyAssignable = false,
  sortBy = sortByOptions[0],
  showAssignBtn = false,
  showPreviewBtn = false
}) => {
  const index = useMemo(() => lunr.Index.load(lunrIndex), [lunrIndex]);
  const contentTypes = useMemo(() => content.map(d => d.entity).filter((e, i, a) => a.indexOf(e) === i).sort().sort((a, b) => [a, b].map(e => e !== 'Lesson').reduce((x, y) => x - y)), [content]);
  const [tab, setTab] = useHistoryState('searchpage-tab', 'all');
  const {
    user,
    access
  } = useSession();
  const userState = user.state?.abbreviation;
  const isAdmin = access.system;
  const searchResults = useMemo(() => index?.query(q => {
    q.term(difference(languageOptions.flatMap(opt => opt.searchVal), languageOptions.filter(opt => languages.includes(opt.value)).flatMap(opt => opt.searchVal)), {
      fields: ['language'],
      presence: lunr.Query.presence.PROHIBITED
    });
    Object.entries(SearchFields).forEach(([field, weight]) => {
      q.term(tokenizer(query), {
        fields: [field],
        boost: weight,
        editDistance: 0
      });
    });
  }) ?? [], [index, query, languages]);
  const rules = useMemo(() => {
    return searchResults.map(({
      ref
    }) => {
      const {
        type,
        slug
      } = content[ref];
      return {
        pathname: `/toolkits/${type}/[slug]`,
        query: {
          slug
        }
      };
    }).map(link => AC.ruleFromHref(link));
  }, [searchResults]);
  const {
    isPending,
    accessible
  } = AC.useQueries(rules);
  const filteredResults = useMemo(() => searchResults.filter((_, index) => accessible[index]).filter(({
    ref
  }) => grades.length === 0 || grades.some(gid => content[ref].grades?.map(g => g.id).includes(gid))).filter(({
    ref
  }) => skills.length === 0 || skills.some(v => content[ref].skills?.includes(v))).filter(({
    ref
  }) => subDomains.length === 0 || subDomains.some(v => content[ref].subDomains?.includes(v))).filter(({
    ref
  }) => domains.length === 0 || domains.some(v => content[ref].domains?.includes(v))).filter(({
    ref
  }) => !onlyAssignable || !!getResourceAssignmentData(content[ref])).filter(({
    ref
  }) => isAdmin || !CONTENT_KEYWORD_FILTER_BY_STATE.find(({
    state
  }) => state.test(userState))?.keywords.some(keyword => keyword.test(content[ref].keywords))), [searchResults, accessible, grades, skills, subDomains, domains, onlyAssignable, userState, isAdmin]);
  const sortedSearchResults = useMemo(() => chain(filteredResults).map(({
    ref,
    score,
    matchData
  }) => ({
    doc: content[ref],
    score: score,
    queryMatchData: matchData,
    filtersApplied: {
      grades,
      skills,
      subDomains,
      domains
    }
  })).sortBy(sorters[sortBy]).map(r => r.doc).value(), [filteredResults, sortBy]);
  const groupedSearchResults = useMemo(() => [{
    label: `All (${sortedSearchResults.length})`,
    key: 'all',
    children: <SearchView showAssignBtn={showAssignBtn} showPreviewBtn={showPreviewBtn} contentData={sortedSearchResults} key={`all-${sortedSearchResults.map(r => r.slug).join(':')}`} tab="all" />
  }].concat(contentTypes.map(t => {
    const results = sortedSearchResults.filter(r => r.entity === t);
    return {
      label: `${t}s (${results.length})`,
      key: t,
      disabled: results.length === 0,
      children: <SearchView showAssignBtn={showAssignBtn} showPreviewBtn={showPreviewBtn} contentData={results} key={`${t}-${results.map(r => r.slug).join(':')}`} tab={t} />
    };
  })).filter(tab => !(hideEmptyTabs && tab.disabled)), [contentTypes, sortedSearchResults, hideEmptyTabs]);
  useEffect(() => {
    groupedSearchResults.find(forTab => forTab.key === tab).disabled && setTab('all');
  }, [tab, setTab, groupedSearchResults]);
  if (isPending) {
    return <Skeleton />;
  }
  return sortedSearchResults.length === 0 ? <Empty description="No results found" /> : <Tabs type="card" activeKey={tab} onChange={setTab} items={groupedSearchResults} />;
};
export default cmsFactory(Search, '/content/content-search-index.json');